import { jsx, jsxs, Fragment } from 'theme-ui/jsx-runtime';
import React, { useRef, useEffect } from 'react';
import { useThemeUI, get, merge, ThemeProvider, Button as Button$2, Box as Box$1, Text, Avatar as Avatar$1, Divider as Divider$1, createElement, Spinner, Badge as Badge$1 } from 'theme-ui';
import { isUndefined } from 'lodash';
import { useFloating, autoUpdate, offset, arrow, flip, useTransitionStyles, useHover, useRole, useInteractions, FloatingPortal, FloatingArrow } from '@floating-ui/react';
import { createPortal } from 'react-dom';
import { Transition } from 'react-transition-group';

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var colors = {
    text: '#000',
    background: '#fff',
    primary: '#4DF7B9',
    secondary: '#00C795',
    gray: '#555',
    lightGrey: '#eee',
    white: '#fff',
    error: 'red',
    inactive: '#666',
    border: '#CDCDCD',
    gradients: {
        black: 'linear-gradient(90deg, #000 0%, #4E4E4E 100%)',
        aqua: 'linear-gradient(90deg, #4DF7B9 0%, #4DF6DC 100%)'
    },
    voice: {
        ownedMedia: '#FFB836',
        media: '#7189FF',
        influencer: '#FF805F',
        celebrity: '#63C8FF',
        partner: '#B697F4',
    },
    grey: {
        200: '#F5F5F5',
        300: '#EEEEEE',
        400: '#EAEAEA',
        500: '#DEDEDE',
        600: '#9B9B9B',
        700: '#424242',
        800: '#2F2F2F',
        900: '#212121'
    },
    aqua: {
        1: '#00C795',
        2: '#3AD4A7',
        3: '#5FE0B7',
        4: '#E7FFF6'
    },
    mono: {
        0: '#000000',
        1: '#939597',
        2: '#E6E7E8'
    }
};

var makeTheme = function (t) { return t; };
var mainFont = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"';
var theme = makeTheme({
    fonts: {
        body: mainFont,
        heading: mainFont,
        monospace: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
        sans: mainFont
    },
    fontSizes: [10, 12, 14, 16, 20, 24, 30, 38, 46],
    fontWeights: {
        regular: 400,
        medium: 500,
        semibold: 600,
        bold: 700
    },
    lineHeights: ['16px', '18px', '22px', '24px', '26px', '32px', '38px', '48px', '60px', '74px'],
    zIndices: {
        card: 5,
        overlay: 10,
        tooltip: 9999
    },
    shadows: {
        '1dp': '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 1px rgba(0, 0, 0, 0.14), 0px 2px 1px rgba(0, 0, 0, 0.20)',
        '2dp': '0px 1px 5px rgba(0, 0, 0, 0.20), 0px 3px 4px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.14))',
        '3dp': '0px 1px 8px rgba(0, 0, 0, 0.20), 0px 3px 4px rgba(0, 0, 0, 0.12), 0px 3px 3px rgba(0, 0, 0, 0.14)',
        '4dp': '0px 1px 10px rgba(0, 0, 0, 0.20), 0px 4px 5px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(0, 0, 0, 0.14)',
        '6dp': '0px 3px 5px rgba(0, 0, 0, 0.20), 0px 1px 18px rgba(0, 0, 0, 0.12), 0px 6px 10px rgba(0, 0, 0, 0.14)',
        '8dp': '0px 4px 5px rgba(0, 0, 0, 0.20), 0px 3px 14px rgba(0, 0, 0, 0.12), 0px 8px 10px rgba(0, 0, 0, 0.14)',
        '9dp': '0px 5px 6px rgba(0, 0, 0, 0.20), 0px 3px 16px rgba(0, 0, 0, 0.12), 0px 9px 12px rgba(0, 0, 0, 0.14)',
        '12dp': '0px 7px 8px rgba(0, 0, 0, 0.20), 0px 5px 22px rgba(0, 0, 0, 0.12), 0px 12px 17px rgba(0, 0, 0, 0.14)',
        '16dp': '0px 8px 10px rgba(0, 0, 0, 0.20), 0px 6px 30px rgba(0, 0, 0, 0.12), 0px 16px 24px rgba(0, 0, 0, 0.14)',
        '24dp': '0px 11px 15px rgba(0, 0, 0, 0.20), 0px 9px 46px rgba(0, 0, 0, 0.12), 0px 24px 38px rgba(0, 0, 0, 0.14)'
    },
    transitions: {
        property: {
            none: 'none',
            all: 'all',
            colors: 'background-color, border-color, color, fill, stroke',
            opacity: 'opacity',
            shadow: 'box-shadow',
            transform: 'transform'
        },
        type: {
            linear: 'linear',
            in: 'cubic-bezier(0.4, 0, 1, 1)',
            out: 'cubic-bezier(0, 0, 0.2, 1)',
            inOut: 'cubic-bezier(0.4, 0, 0.2, 1)'
        },
        duration: {
            '225': '225ms'
        }
    },
    radii: {
        rounded: 8,
        circle: '100%',
        square: 0
    },
    borders: {
        default: '1px solid'
    },
    text: {
        default: {
            variant: 'text.body1'
        },
        h1: {
            fontFamily: 'heading',
            fontSize: 8 // 46px
        },
        h2: {
            fontFamily: 'heading',
            fontSize: 7 // 38px
        },
        h3: {
            fontFamily: 'heading',
            fontSize: 6 // 30px
        },
        h4: {
            fontFamily: 'heading',
            fontSize: 5 // 24px
        },
        h5: {
            fontFamily: 'heading',
            fontSize: 4 // 20px
        },
        h6: {
            fontFamily: 'heading',
            fontSize: 3,
            fontWeight: 'medium',
            lineHeight: 3
        },
        body1: {
            fontSize: 3,
            fontFamily: 'body'
        },
        body2: {
            fontSize: 2,
            fontFamily: 'body'
        },
        caption: {
            fontSize: 1,
            fontFamily: 'body'
        },
        overline: {
            fontSize: 0,
            fontFamily: 'body'
        },
        p: {
            variant: 'text.body1'
        },
        span: {
            variant: 'text.body2',
            lineHeight: 2
        }
    },
    tables: {
        simple: {
            borderSpacing: 0,
            borderCollapse: 'separate',
            color: 'grey.900',
            thead: {
                th: {
                    fontSize: 1,
                    fontFamily: 'heading',
                    textTransform: 'uppercase',
                    fontStyle: 'normal',
                    fontWeight: 'bold',
                    lineHeight: 1,
                    padding: '19px 8px',
                    borderBottomWidth: '1px',
                    borderBottomStyle: 'solid',
                    borderBottomColor: 'grey.400',
                    textAlign: 'left'
                }
            },
            tbody: {
                td: {
                    fontFamily: 'body',
                    fontSize: 2,
                    fontStyle: 'normal',
                    fontWeight: 'regular',
                    lineHeight: 3,
                    padding: '8px',
                    borderBottomWidth: '1px',
                    borderBottomStyle: 'solid',
                    borderBottomColor: 'grey.400'
                },
                'tr:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                },
                'tr:last-child td': {
                    borderBottom: 'none'
                }
            }
        }
    },
    boxes: {
        center: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
        }
    },
    colors: colors,
    spinner: {
        color: 'secondary'
    },
    buttons: {
        sizes: {
            small: {
                minHeight: 32,
                fontSize: 1
            },
            medium: {
                minHeight: 40,
                fontSize: 2
            },
            large: {
                minHeight: 48,
                fontSize: 3
            }
        },
        text: {
            neutral: {
                color: 'grey.900',
                bg: '#fff',
                '&:hover': {
                    bg: 'grey.200'
                },
                '&:disabled': {
                    color: 'grey.600',
                    bg: '#fff',
                    cursor: 'not-allowed'
                }
            }
        },
        outlined: {
            neutral: {
                color: 'grey.900',
                bg: 'white',
                fontWeight: 'medium',
                border: 'default',
                borderColor: 'grey.600',
                '&:hover': {
                    bg: 'grey.200'
                },
                '&:disabled': {
                    color: 'grey.600',
                    bg: '#fff',
                    cursor: 'not-allowed'
                }
            }
        }
    },
    badges: {
        default: {
            fontWeight: '500',
            fontSize: '12px',
            fontFamily: 'body',
            height: '20px',
            borderRadius: '16px',
            padding: '0px 8px',
            textAlign: 'center',
            lineHeight: '20px',
            color: 'grey.900',
            background: 'grey.500'
        },
        beta: {
            variant: 'badges.default',
            background: 'gradients.aqua'
        },
        ownedMedia: {
            variant: 'badges.default',
            background: 'voice.ownedMedia',
            color: 'white'
        },
        media: {
            variant: 'badges.default',
            background: 'voice.media',
            color: 'white'
        },
        influencer: {
            variant: 'badges.default',
            background: 'voice.influencer',
            color: 'white'
        },
        celebrity: {
            variant: 'badges.default',
            background: 'voice.celebrity',
            color: 'white'
        },
        partner: {
            variant: 'badges.default',
            background: 'voice.partner',
            color: 'white'
        }
    },
    images: {
        avatar: {
            rounded: {
                width: 'avatar.large',
                borderRadius: 'rounded',
                border: 'default',
                borderColor: 'border'
            },
            circle: {
                width: 'avatar.large',
                borderRadius: 'circle',
                border: 'default',
                borderColor: 'border'
            },
            square: {
                width: 'avatar.large',
                borderRadius: 'square',
                border: 'default',
                borderColor: 'border'
            }
        }
    },
    sizes: {
        avatar: {
            large: 65,
            small: 45
        }
    },
    divider: {
        vertical: {
            borderLeftWidth: '1px',
            borderColor: 'mono.2',
            alignSelf: 'stretch'
        },
        horizontal: {
            borderBottomWidth: '1px',
            borderColor: 'mono.2',
            width: '100%'
        },
        dashed: {
            borderStyle: 'dashed'
        },
        solid: {
            borderStyle: 'solid'
        }
    }
});
var useLMTheme = useThemeUI;
var getThemeProp = get;
var getFromTheme = function (prop) { return function (t) { return getThemeProp(t, prop); }; };
var getFontSizeValue = function (index) { return theme.fontSizes[index]; };

var LMThemeProvider = function LMThemeProvider(props) {
    var children = props.children, themeOverrides = props.themeOverrides;
    var theme$1 = React.useMemo(function () { return merge(theme, themeOverrides || {}); }, [themeOverrides]);
    return jsx(ThemeProvider, __assign({ theme: theme$1 }, { children: children }));
};
var LMThemeProvider$1 = React.memo(LMThemeProvider);

var withDefaultStylePropsCoreHOC = function (passReference) {
    return function (Component, defaultStyleProps) {
        return React.forwardRef(function WithDefaultStyleProps(props, ref) {
            var styleProps = React.useMemo(function () { return (__assign(__assign({}, defaultStyleProps), props.styleProps)); }, [props.styleProps]);
            return jsx(Component, __assign({ ref: passReference ? ref : undefined }, props, { styleProps: styleProps }));
        });
    };
};
var withDefaultStyleProps = withDefaultStylePropsCoreHOC(true);
var withDefaultStylePropsNoRef = withDefaultStylePropsCoreHOC(false);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var buildLMComponent = function (renderFn, defaultStyleProps) {
    var Component;
    if (renderFn.length > 1) {
        var forwardedComponent = React.forwardRef(renderFn);
        Component = withDefaultStyleProps(forwardedComponent, defaultStyleProps);
    }
    else
        Component = withDefaultStylePropsNoRef(renderFn, defaultStyleProps);
    Component.displayName = renderFn.name;
    return Component;
};

var Button = buildLMComponent(function Button(props, ref) {
    var styleProps = props.styleProps, _a = props.variant, variant = _a === void 0 ? 'text' : _a, _b = props.color, color = _b === void 0 ? 'neutral' : _b, _c = props.size, size = _c === void 0 ? 'medium' : _c, _d = props.disabled, disabled = _d === void 0 ? false : _d, rest = __rest(props, ["styleProps", "variant", "color", "size", "disabled"]);
    var theme = useLMTheme().theme;
    return (jsx(Button$2, __assign({ ref: ref, variant: "buttons.".concat(variant, ".").concat(color), sx: __assign(__assign(__assign({}, getThemeProp(theme, "buttons.sizes.".concat(size))), { cursor: 'pointer' }), styleProps), disabled: disabled }, rest)));
});
var Button$1 = React.memo(Button);

var defaultStyleProps$1 = {
    position: 'relative'
};
var Box = buildLMComponent(function Box(props, ref) {
    var styleProps = props.styleProps, rest = __rest(props, ["styleProps"]);
    return jsx(Box$1, __assign({ ref: ref }, rest, { sx: styleProps }));
}, defaultStyleProps$1);

var Typography = buildLMComponent(function Typography(props, ref) {
    var align = props.align, _a = props.as, as = _a === void 0 ? 'p' : _a, variant = props.variant, styleProps = props.styleProps, children = props.children, id = props.id, rest = __rest(props, ["align", "as", "variant", "styleProps", "children", "id"]);
    var theme = useLMTheme().theme;
    var selectedComponent = as || 'p';
    var variantKey = variant ? variant : selectedComponent.toString();
    var variantExists = !!get(theme, "text.".concat(variantKey));
    return (jsx(Text, __assign({ id: id, as: selectedComponent, ref: ref, variant: variantExists ? variantKey : 'default', sx: __assign({ textAlign: align }, styleProps) }, rest, { children: children })));
});

var SvgIcon = buildLMComponent(function SvgIcon(props, ref) {
    var color = props.color, title = props.title, _a = props.viewBox, viewBox = _a === void 0 ? '0 0 24 24' : _a, children = props.children, styleProps = props.styleProps, Component = props.Component, rest = __rest(props, ["color", "title", "viewBox", "children", "styleProps", "Component"]);
    var hasSvgAsChild = React.isValidElement(children) && children.type === 'svg';
    var SvgComponent = Component ? Component : 'svg';
    return (jsxs(SvgComponent, __assign({ "aria-hidden": title ? undefined : true, role: title ? 'img' : undefined, viewBox: viewBox, width: '1em', height: '1em', strokeWidth: 1.5, fontSize: 24, color: color, fill: hasSvgAsChild ? undefined : 'currentColor', display: 'inline-block' }, rest, { sx: __assign(__assign({}, styleProps), { flexShrink: 0 }) }, (hasSvgAsChild ? children.props : {}), { ref: ref }, { children: [title && jsx("title", { children: title }), hasSvgAsChild ? children.props.children : children] })));
});

var TabsContext = React.createContext(null);

var baseButtonStyleProps = {
    padding: '8px 16px',
    border: 'none',
    background: 'none',
    borderBottom: '2px solid',
    borderBottomColor: 'lightGrey',
    width: 'fit-content'
};
var baseLabelStyleProps = {
    fontWeight: 'medium',
    lineHeight: 2,
    color: 'black'
};

var Tab = buildLMComponent(function Tab(props) {
    var label = props.label, _a = props.disabled, disabled = _a === void 0 ? false : _a, _b = props.active, activeProp = _b === void 0 ? false : _b, onClick = props.onClick, styleProps = props.styleProps, labelStyleProps = props.labelStyleProps, value = props.value;
    var context = React.useContext(TabsContext);
    var active = React.useMemo(function () { return activeProp || (!isUndefined(value) && (context === null || context === void 0 ? void 0 : context.value) === value); }, [context, value, activeProp]);
    var dynamicButtonStyleProps = React.useMemo(function () {
        return {
            borderBottomColor: disabled || !active ? 'lightGrey' : 'primary',
            cursor: disabled ? 'not-allowed' : 'pointer'
        };
    }, [disabled, active]);
    var dynamicLabelStyleProps = React.useMemo(function () {
        return {
            color: disabled ? 'lightGrey' : !active ? 'inactive' : 'black'
        };
    }, [disabled, active]);
    var handleClick = React.useCallback(function (ev) {
        if (context && !isUndefined(value))
            context.onSelected(value);
        if (onClick)
            onClick(ev);
    }, [context, onClick, value]);
    return (jsx("button", __assign({ sx: __assign(__assign(__assign({}, baseButtonStyleProps), dynamicButtonStyleProps), styleProps), role: "tab", "aria-selected": active, disabled: disabled, onClick: handleClick }, { children: jsx(Typography, __assign({ variant: "span", as: 'span', styleProps: __assign(__assign(__assign({}, baseLabelStyleProps), dynamicLabelStyleProps), labelStyleProps) }, { children: label })) })));
});

var Link = buildLMComponent(function Link(props, ref) {
    var styleProps = props.styleProps, children = props.children, href = props.href, target = props.target, _a = props.as, as = _a === void 0 ? 'a' : _a, _b = props.variant, variant = _b === void 0 ? 'default' : _b, rest = __rest(props, ["styleProps", "children", "href", "target", "as", "variant"]);
    var linkProps = { href: href, target: target };
    return (jsx(Typography, __assign({ ref: ref }, linkProps, { as: as, variant: variant, role: "link", sx: __assign({ color: 'aqua.1', cursor: 'pointer', textDecoration: 'underline' }, styleProps) }, rest, { children: children })));
});

var Avatar = buildLMComponent(function Avatar(props) {
    var src = props.src, _a = props.variant, variant = _a === void 0 ? 'rounded' : _a, styleProps = props.styleProps, _b = props.border, border = _b === void 0 ? true : _b, _c = props.size, size = _c === void 0 ? 'large' : _c, rest = __rest(props, ["src", "variant", "styleProps", "border", "size"]);
    return (jsx(Avatar$1, __assign({ src: src, variant: "avatar.".concat(variant), sx: __assign({ border: !border ? 'none' : undefined, width: "avatar.".concat(size), height: "avatar.".concat(size) }, styleProps) }, rest)));
});

var Divider = buildLMComponent(function Divider(props) {
    var _a = props.variant, variant = _a === void 0 ? 'solid' : _a, _b = props.orientation, orientation = _b === void 0 ? 'vertical' : _b, styleProps = props.styleProps, rest = __rest(props, ["variant", "orientation", "styleProps"]);
    var theme = useLMTheme().theme;
    return jsx(Divider$1, __assign({ sx: __assign(__assign(__assign({}, get(theme, "divider.".concat(variant))), get(theme, "divider.".concat(orientation))), styleProps) }, rest));
});

var useTimeout = function () {
    var timeoutId = React.useRef();
    var clear = React.useCallback(function () {
        if (timeoutId.current)
            clearTimeout(timeoutId.current);
    }, []);
    var run = React.useCallback(function (callback, delay) {
        clear();
        timeoutId.current = setTimeout(callback, delay);
    }, [clear]);
    React.useEffect(function () {
        return function () {
            clear();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return { run: run, clear: clear };
};

var tooltipStyles$2 = {
    padding: '8px 16px',
    borderRadius: 4
};

var ARROW_HEIGHT = 5;
var GAP = 4;
var TRANSITION_DURATION = 150;
var TooltipContent = buildLMComponent(function (props, ref) {
    var title = props.title, content = props.content, styleProps = props.styleProps, labelStyleProps = props.labelStyleProps, rest = __rest(props, ["title", "content", "styleProps", "labelStyleProps"]);
    var theme = useLMTheme().theme;
    var color = (styleProps === null || styleProps === void 0 ? void 0 : styleProps.backgroundColor) || getThemeProp(theme, 'colors.grey.900');
    return (jsxs(Box, __assign({ role: 'tooltip-content', styleProps: __assign(__assign({ backgroundColor: color }, tooltipStyles$2), styleProps), ref: ref }, rest, { children: [title &&
                (typeof title === 'string' ? (jsx(Typography, __assign({ variant: "h6", as: "h6", styleProps: { whiteSpace: 'pre-line', labelStyleProps: labelStyleProps }, color: "white" }, { children: title }))) : (title)), typeof content === 'string' ? (jsx(Typography, __assign({ variant: "span", as: "span", styleProps: labelStyleProps, color: "white" }, { children: content }))) : (content)] })));
});
var Tooltip = buildLMComponent(function Tooltip(props) {
    var children = props.children, title = props.title, content = props.content, _a = props.placement, placement = _a === void 0 ? 'top' : _a, styleProps = props.styleProps, labelStyleProps = props.labelStyleProps, _b = props.arrow, arrow$1 = _b === void 0 ? true : _b, open = props.open, portalRef = props.portalRef, onOpen = props.onOpen, onClose = props.onClose, rest = __rest(props, ["children", "title", "content", "placement", "styleProps", "labelStyleProps", "arrow", "open", "portalRef", "onOpen", "onClose"]);
    var _c = React.useState(false), isOpen = _c[0], setIsOpen = _c[1];
    var _d = useTimeout(), run = _d.run, clear = _d.clear;
    var arrowRef = React.useRef(null);
    var theme = useLMTheme().theme;
    React.useEffect(function () {
        if (!isUndefined(open) && isOpen !== open)
            setIsOpen(open);
    }, [open, isOpen]);
    var handleClose = React.useCallback(function (event) {
        run(function () {
            setIsOpen(false);
            if (onClose && isOpen)
                onClose(event);
        }, 100);
    }, [isOpen, onClose, run]);
    var handleOpenChange = React.useCallback(function (open, event) {
        clear();
        open ? setIsOpen(open) : handleClose(event);
        if (onOpen && open && !isOpen)
            onOpen(event);
    }, [setIsOpen, onOpen, isOpen, handleClose, clear]);
    var handleEnter = React.useCallback(function (e) {
        handleOpenChange(true, e);
    }, [handleOpenChange]);
    var handleLeave = React.useCallback(function (e) {
        handleOpenChange(false, e);
    }, [handleOpenChange]);
    var _e = useFloating({
        placement: placement,
        open: !isUndefined(open) ? open : isOpen,
        onOpenChange: handleOpenChange,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(ARROW_HEIGHT + GAP),
            arrow({
                element: arrowRef,
                padding: ARROW_HEIGHT + GAP
            }),
            flip({
                fallbackAxisSideDirection: 'start',
                padding: ARROW_HEIGHT + GAP
            })
        ]
    }), refs = _e.refs, floatingStyles = _e.floatingStyles, context = _e.context, computedPlacement = _e.placement;
    var _f = useTransitionStyles(context, {
        duration: TRANSITION_DURATION
    }), isMounted = _f.isMounted, styles = _f.styles;
    var hover = useHover(context);
    var role = useRole(context, { role: 'tooltip' });
    var _g = useInteractions([hover, role]), getReferenceProps = _g.getReferenceProps, getFloatingProps = _g.getFloatingProps;
    var arrowStyles = React.useMemo(function () {
        // This is ensuring the style of the arrow when the placement is changed from the initial one
        if (computedPlacement.includes('right')) {
            return { left: undefined };
        }
        else if (computedPlacement.includes('left')) {
            return { right: undefined };
        }
        else if (computedPlacement.includes('top')) {
            return { bottom: undefined };
        }
        else if (computedPlacement.includes('bottom')) {
            return { top: undefined };
        }
    }, [computedPlacement]);
    var color = (styleProps === null || styleProps === void 0 ? void 0 : styleProps.backgroundColor) || getThemeProp(theme, 'colors.grey.900');
    return (jsxs(Fragment, { children: [React.cloneElement(children, __assign({ ref: refs.setReference }, getReferenceProps())), isMounted && (jsx(FloatingPortal, __assign({ root: portalRef }, { children: jsxs(Box, __assign({ ref: refs.setFloating }, rest, { styleProps: __assign(__assign(__assign({}, styles), floatingStyles), { zIndex: 'tooltip' }) }, getFloatingProps(), { onMouseEnter: handleEnter, onMouseLeave: handleLeave }, { children: [arrow$1 && (jsx(FloatingArrow, { ref: arrowRef, context: context, height: ARROW_HEIGHT, fill: color, style: __assign({}, arrowStyles), name: "tooltipArrow" })), jsx(TooltipContent, { title: title, content: content, styleProps: styleProps, labelStyleProps: labelStyleProps })] })) })))] }));
});

var defaultMetric = {
    threshold: 1000,
    showDecimalIfInteger: false
};
var currencySymbols = {
    EUR: '€',
    USD: '$',
    GBP: '£',
    JPY: '¥',
    CNY: '¥'
};
var ranges = [
    {
        divider: 1e6,
        suffix: 'M',
        decimal: 1
    },
    {
        divider: 1e3,
        suffix: 'K',
        decimal: 0
    }
];
var toMetric = function (value, _a) {
    var _b = _a === void 0 ? defaultMetric : _a, decimals = _b.decimals, _c = _b.threshold, threshold = _c === void 0 ? 1000 : _c, showDecimalIfInteger = _b.showDecimalIfInteger;
    var n = value;
    var numberRange = ranges.find(function (range) { return n >= range.divider; });
    if (n < threshold || !numberRange) {
        if (n % 1 === 0) {
            if (showDecimalIfInteger) {
                return n.toFixed(decimals);
            }
            return n.toString();
        }
        return n.toFixed(decimals);
    }
    var choosenDecimals = decimals === undefined ? numberRange.decimal : decimals;
    var parsedNumber = n / numberRange.divider;
    return parsedNumber.toFixed(choosenDecimals) + numberRange.suffix;
};
var toCurrencyDefaultParams = {
    currency: 'EUR',
    baseCurrency: 'EUR',
    currencyPosition: 'end'
};
var toCurrency = function (n, customParams) {
    // This method could also convert the values in the future. For now it only display the parsed value with the symbol requested
    var params = __assign(__assign({}, toCurrencyDefaultParams), customParams);
    var value = toMetric(n, params.metricParams);
    var symbol = currencySymbols[params.currency];
    return params.currencyPosition === 'start' ? "".concat(symbol).concat(value) : "".concat(value).concat(symbol);
};
var useNumberFormatter = function () {
    return { toMetric: toMetric, toCurrency: toCurrency };
};

function createSvgIcon(path, displayName, viewBox) {
    if (viewBox === void 0) { viewBox = '0 0 24 24'; }
    function Component(props, ref) {
        return (jsx(SvgIcon, __assign({ "data-testid": "".concat(displayName, "Icon"), ref: ref, viewBox: viewBox }, props, { children: path })));
    }
    Component.displayName = "".concat(displayName, "Icon");
    return React.memo(React.forwardRef(Component));
}

var InfoOutlined = createSvgIcon(jsx("path", { fill: "currentColor", d: "M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" }), 'InfoOutlined');

var defaultStyleProps = {
    display: 'flex',
    width: '100%',
    minHeight: 0
};
var firstSectionStyle = {
    minWidth: '20%',
    position: 'relative',
    padding: '0px 0px 40px 0'
};
var sectionStyle = {
    display: 'flex',
    flex: 1,
    paddingTop: 0,
    paddingBottom: 30,
    position: 'relative'
};
var svgStyle = {
    position: 'relative',
    marginTop: 18,
    height: 'calc(100% - 18px)',
    width: '100%'
};
var labelStyle = {
    position: 'absolute',
    right: 12,
    color: 'mono.1',
    display: 'flex',
    alignItems: 'center'
};
var percentageStyle = {
    fontWeight: 'medium',
    color: 'mono.1'
};
var valueStyle = {
    textAlign: 'end',
    color: 'mono.1'
};
var tooltipStyles$1 = {
    maxWidth: 200
};
var infoIconStyles$1 = {
    fontSize: 18,
    marginLeft: '4px'
};

// Add colors to support more than 4 sections showing
var defaultColors = ['aqua.1', 'aqua.2', 'aqua.3', 'aqua.4'];
var Funnel = buildLMComponent(function Funnel(props, ref) {
    var id = props.id, data = props.data, totalValue = props.totalValue, colors = props.colors, styleProps = props.styleProps, labelStyleProps = props.labelStyleProps, percentageStyleProps = props.percentageStyleProps, valueStyleProps = props.valueStyleProps, rest = __rest(props, ["id", "data", "totalValue", "colors", "styleProps", "labelStyleProps", "percentageStyleProps", "valueStyleProps"]);
    var toMetric = useNumberFormatter().toMetric;
    var theme = useLMTheme().theme;
    var funnelColors = React.useMemo(function () { return colors || defaultColors.map(function (color) { return get(theme, "colors.".concat(color)); }); }, [theme, colors]);
    var defaultColor = get(theme, 'colors.aqua.1');
    var _a = React.useMemo(function () {
        var totalNumber = totalValue || Math.max.apply(Math, data.map(function (data) { return data.value; }));
        return {
            totalNumber: totalNumber,
            basePercentage: Math.ceil((data[0].value / totalNumber) * 100)
        };
    }, [data, totalValue]), totalNumber = _a.totalNumber, basePercentage = _a.basePercentage;
    var funnelSectionsProps = React.useMemo(function () {
        var baseOffset = Math.max(0, Math.floor((100 - basePercentage) / 2));
        var y0 = Math.min(50, baseOffset);
        var y3 = Math.max(50, 100 - baseOffset);
        var sectionProps = [];
        for (var i = 1; i < data.length; i++) {
            var percentage = Math.ceil((data[i].value / totalNumber) * 100);
            var offset = Math.max(0, Math.floor((100 - percentage) / 2));
            var y1 = Math.min(50, offset);
            var y2 = Math.max(50, 100 - offset);
            sectionProps.push({ y0: y0, y1: y1, y2: y2, y3: y3, percentage: percentage, label: data[i].label, value: data[i].value || 0, info: data[i].info });
            y0 = y1;
            y3 = y2;
        }
        return sectionProps;
    }, [data, totalNumber, basePercentage]);
    var displayLabelAndValue = React.useCallback(function (label, percentage, value, info) {
        return (jsxs(Fragment, { children: [jsxs(Typography, __assign({ variant: "caption", styleProps: __assign(__assign({}, labelStyle), labelStyleProps), id: "".concat(id, "-label") }, { children: [label, info && (jsx(Tooltip, __assign({ content: info, placement: "bottom", styleProps: tooltipStyles$1, id: "".concat(id, "-info") }, { children: jsx(InfoOutlined, { styleProps: infoIconStyles$1 }) })))] })), jsxs(Box, __assign({ sx: { position: 'absolute', right: 12, bottom: 0, textAlign: 'end' } }, { children: [jsxs(Typography, __assign({ variant: "caption", styleProps: __assign(__assign({}, percentageStyle), percentageStyleProps), id: "".concat(id, "-percentage") }, { children: [percentage, "%"] })), jsx(Typography, __assign({ variant: "caption", styleProps: __assign(__assign({}, valueStyle), valueStyleProps), id: "".concat(id, "-value") }, { children: toMetric(value) }))] }))] }));
    }, [labelStyleProps, id, percentageStyleProps, valueStyleProps, toMetric]);
    return (jsxs(Box, __assign({ styleProps: __assign({}, styleProps) }, rest, { ref: ref, id: id }, { children: [jsx(Box, __assign({ styleProps: firstSectionStyle }, { children: displayLabelAndValue(data[0].label, basePercentage, data[0].value, data[0].info) })), jsx(Divider, { variant: "dashed" }), funnelSectionsProps.map(function (props, index) {
                return (jsxs(React.Fragment, { children: [jsxs(Box, __assign({ styleProps: sectionStyle }, { children: [jsxs("svg", __assign({ viewBox: '0 0 100 100', preserveAspectRatio: "none", sx: svgStyle }, { children: [jsx("title", { children: "section_".concat(index) }), jsx("polygon", { fill: funnelColors[index] || defaultColor, points: "0,".concat(props.y0, " 100,").concat(props.y1, " 100,").concat(props.y2, " 0,").concat(props.y3), sx: { strokeWidth: 0 } })] })), displayLabelAndValue(props.label, props.percentage, props.value, props.info)] })), jsx(Divider, { variant: "dashed" })] }, "section_".concat(index)));
            })] })));
}, defaultStyleProps);

var TabsProvider = function (props) {
    var tabsProps = props.tabsProps, children = props.children;
    var value = tabsProps.value, onSelected = tabsProps.onSelected;
    return jsx(TabsContext.Provider, __assign({ value: { value: value, onSelected: onSelected } }, { children: children }));
};

var baseTabsStyleProps = {
    display: 'flex',
    alignItems: 'center',
    width: 'fit-content'
};
var baseSeparatorProps = {
    width: 1,
    height: 16,
    backgroundColor: 'lightGrey',
    marginRight: '4px'
};

var Tabs = buildLMComponent(function Tabs(props) {
    var value = props.value, onSelected = props.onSelected, children = props.children, styleProps = props.styleProps, rest = __rest(props, ["value", "onSelected", "children", "styleProps"]);
    return (jsx(TabsProvider, __assign({ tabsProps: { value: value, onSelected: onSelected } }, { children: jsx(Box, __assign({ sx: __assign(__assign({}, baseTabsStyleProps), styleProps) }, rest, { children: React.Children.map(children, function (child, index) {
                return (jsxs(Fragment, { children: [child, index !== React.Children.count(children) - 1 && jsx("div", { sx: baseSeparatorProps })] }));
            }) })) })));
});

var Profile = buildLMComponent(function Profile(props) {
    var title = props.title, subtitle = props.subtitle, description = props.description, image = props.image, _a = props.variant, variant = _a === void 0 ? 'header' : _a, avatarVariant = props.avatarVariant, styleProps = props.styleProps, rest = __rest(props, ["title", "subtitle", "description", "image", "variant", "avatarVariant", "styleProps"]);
    var descriptionContent = description ? (jsx(Typography, __assign({ as: 'span', styleProps: { color: 'grey', fontWeight: 400, lineHeight: '22px' }, id: "profile-description" }, { children: description }))) : null;
    var headerContent = (jsxs(Box, { children: [jsx(Typography, __assign({ as: 'h4', styleProps: { fontWeight: 700, lineHeight: '38px' }, id: "profile-header-title" }, { children: title })), jsx(Typography, __assign({ as: 'p', styleProps: { marginBottom: '8px', fontWeight: 500, lineHeight: '22px' }, id: "profile-header-subtitle" }, { children: subtitle })), descriptionContent] }));
    var headerAvatar = jsx(Avatar, { variant: avatarVariant || 'rounded', src: image, styleProps: { marginRight: 16 } });
    var contactAvatar = jsx(Avatar, { variant: avatarVariant || 'circle', src: image, size: "small", styleProps: { marginRight: 16 } });
    var contactContent = (jsxs(Box, { children: [jsx(Typography, __assign({ as: 'p', styleProps: { fontWeight: 500, lineHeight: '22px' }, id: "profile-contact-title" }, { children: title })), jsx(Typography, __assign({ styleProps: { marginBottom: '8px', fontWeight: 500, lineHeight: '16px', fontSize: '12px' } }, { children: subtitle })), descriptionContent] }));
    return (jsx(Box, __assign({}, rest, { styleProps: __assign({ display: 'inline-block' }, styleProps) }, { children: variant === 'header' ? (jsxs(Box, __assign({ styleProps: { display: 'flex', flexDirection: 'row' } }, { children: [image && headerAvatar, headerContent] }))) : (jsxs(Box, __assign({ styleProps: { display: 'flex', flexDirection: 'row' } }, { children: [image && contactAvatar, contactContent] }))) })));
});

var keyMetricContainerStyles = {
    display: 'inline-flex',
    flexDirection: 'column',
    textAlign: 'center'
};
var titleStyles = {
    fontSize: 12,
    color: 'grey.600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
};
var tooltipStyles = {
    maxWidth: 200
};
var infoIconStyles = {
    fontSize: 18,
    marginLeft: '4px'
};
var valueStyles = {
    fontWeight: 700,
    fontSize: 18,
    color: 'grey.900'
};

var KeyMetric = buildLMComponent(function KeyMetric(props, ref) {
    var id = props.id, title = props.title, value = props.value, info = props.info, titleStyleProps = props.titleStyleProps, valueStyleProps = props.valueStyleProps, styleProps = props.styleProps, metricProps = props.metricProps, currencyProps = props.currencyProps, rest = __rest(props, ["id", "title", "value", "info", "titleStyleProps", "valueStyleProps", "styleProps", "metricProps", "currencyProps"]);
    var _a = useNumberFormatter(), toMetric = _a.toMetric, toCurrency = _a.toCurrency;
    var shownValue = React.useMemo(function () {
        if (value === null)
            return '-';
        if (typeof value === 'string')
            return value;
        if (currencyProps)
            return toCurrency(value, __assign(__assign({}, currencyProps), { metricParams: metricProps }));
        return toMetric(value, metricProps);
    }, [currencyProps, metricProps, toCurrency, toMetric, value]);
    return (jsxs(Box, __assign({ id: id, ref: ref }, rest, { styleProps: styleProps }, { children: [jsxs(Typography, __assign({ as: 'span', styleProps: __assign(__assign({}, titleStyles), titleStyleProps), id: "".concat(id, "-title") }, { children: [title, info && (jsx(Tooltip, __assign({ content: info, placement: "top", styleProps: tooltipStyles }, { children: jsx(InfoOutlined, { styleProps: infoIconStyles, id: "".concat(id, "-info") }) })))] })), jsx(Typography, __assign({ as: 'span', styleProps: __assign(__assign({}, valueStyles), valueStyleProps), id: "".concat(id, "-value") }, { children: shownValue }))] })));
}, keyMetricContainerStyles);

var keyMetricsContainer = {
    display: 'flex',
    gap: '10px',
    padding: '5px'
};

var KeyMetrics = buildLMComponent(function KeyMetrics(props, ref) {
    var id = props.id, metrics = props.metrics, styleProps = props.styleProps, _a = props.justify, justify = _a === void 0 ? 'space-evenly' : _a, rest = __rest(props, ["id", "metrics", "styleProps", "justify"]);
    return (jsx(Box, __assign({ id: id, ref: ref }, rest, { styleProps: __assign(__assign(__assign({}, keyMetricsContainer), { justifyContent: justify }), styleProps) }, { children: metrics.map(function (metricProps, index) { return (createElement(KeyMetric, __assign({ id: "".concat(id, "-keyMetric-").concat(metrics[index].title) }, metricProps, { key: index }))); }) })));
});

var Loader = buildLMComponent(function Loader(props) {
    var _a = props.strokeWidth, strokeWidth = _a === void 0 ? 2 : _a, _b = props.size, size = _b === void 0 ? 48 : _b, _c = props.duration, duration = _c === void 0 ? 1000 : _c, styleProps = props.styleProps, rest = __rest(props, ["strokeWidth", "size", "duration", "styleProps"]);
    return jsx(Spinner, __assign({ variant: "spinner", role: "loader" }, { strokeWidth: strokeWidth, size: size, duration: duration }, { sx: styleProps }, rest));
});

var Badge = buildLMComponent(function Badge(props) {
    var label = props.label, styleProps = props.styleProps, _a = props.variant, variant = _a === void 0 ? 'default' : _a, rest = __rest(props, ["label", "styleProps", "variant"]);
    return (jsx(Badge$1, __assign({ variant: "badges.".concat(variant), sx: __assign({}, styleProps) }, rest, { children: label })));
});

var Table = function Table(props) {
    var headers = props.headers, rows = props.rows, _a = props.variant, variant = _a === void 0 ? 'simple' : _a, _b = props.fluid, fluid = _b === void 0 ? false : _b, styleProps = props.styleProps, rest = __rest(props, ["headers", "rows", "variant", "fluid", "styleProps"]);
    // The parent is in charge of making sure the same object is sent to avoid re-renderings.
    var thList = React.useMemo(function () { return (jsx("tr", { children: headers === null || headers === void 0 ? void 0 : headers.map(function (header) { return (jsx("th", { children: header.alias }, header.name.toString())); }) })); }, [headers]);
    // The parent is in charge of making sure the same objects are sent to avoid re-rendering.
    var tdList = React.useMemo(function () {
        return rows === null || rows === void 0 ? void 0 : rows.map(function (row) { return (jsx("tr", { children: headers === null || headers === void 0 ? void 0 : headers.map(function (header) { return (jsx("td", { children: row[header.name] || '' }, header.name.toString())); }) }, row['id'])); });
    }, [headers, rows]);
    return (jsxs("table", __assign({ sx: __assign({ variant: "tables.".concat(variant), width: fluid ? '100%' : 'auto' }, styleProps) }, rest, { children: [jsx("thead", { children: thList }), jsx("tbody", { children: tdList })] })));
};
var Table$1 = buildLMComponent(Table);

/**
 * Usage:
 * const [blockScroll, allowScroll] = useScrollBlock();
 */
var useScrollBlock = function () {
    var scrollBlocked = useRef(false);
    var html = document.documentElement;
    var body = document.body;
    var blockScroll = function () {
        if (!body || !body.style || scrollBlocked.current)
            return;
        if (document == undefined)
            return;
        var scrollBarWidth = window.innerWidth - html.clientWidth;
        var bodyPaddingRight = parseInt(window.getComputedStyle(body).getPropertyValue('padding-right')) || 0;
        /**
         * 1. Fixes a bug in iOS and desktop Safari whereby setting
         *    `overflow: hidden` on the html/body does not prevent scrolling.
         * 2. Fixes a bug in desktop Safari where `overflowY` does not prevent
         *    scroll if an `overflow-x` style is also applied to the body.
         */
        html.style.position = 'relative'; /* [1] */
        html.style.overflow = 'hidden'; /* [2] */
        body.style.position = 'relative'; /* [1] */
        body.style.overflow = 'hidden'; /* [2] */
        body.style.paddingRight = "".concat(bodyPaddingRight + scrollBarWidth, "px");
        scrollBlocked.current = true;
    };
    var allowScroll = function () {
        if (!body || !body.style || !scrollBlocked.current)
            return;
        html.style.position = '';
        html.style.overflow = '';
        body.style.position = '';
        body.style.overflow = '';
        body.style.paddingRight = '';
        scrollBlocked.current = false;
    };
    return [blockScroll, allowScroll];
};

var defaultBackdropStyleProps = {
    display: 'block',
    height: '100vh',
    width: '100vw',
    left: 0,
    position: 'fixed',
    top: 0,
    backgroundColor: 'mono.0'
};
var defaultContainerStyleProps = {
    position: 'fixed',
    visibility: 'visible',
    backgroundColor: 'white',
    minWidth: '200px',
    height: '100vh',
    top: 0,
    padding: '5px',
    display: 'flex',
    width: 'auto'
};

var Drawer = buildLMComponent(function DrawerProps(props) {
    var duration = 225;
    var id = props.id, open = props.open, anchor = props.anchor, onClose = props.onClose, _a = props.zIndex, zIndex = _a === void 0 ? 10 : _a, children = props.children, containerStyleProps = props.containerStyleProps;
    var theme = useLMTheme().theme;
    var transitionDuration = getThemeProp(theme, "transitions.duration.".concat(duration));
    var transitionType = getThemeProp(theme, "transitions.type.inOut");
    var _b = useScrollBlock(), blockScroll = _b[0], allowScroll = _b[1];
    useEffect(function () {
        if (!open)
            return;
        var eventKeyListener = function (event) {
            if (event.key === 'Escape' && onClose) {
                onClose(event, 'escapeKeyDown');
            }
        };
        document.addEventListener('keydown', eventKeyListener);
        return function () {
            document.removeEventListener('keydown', eventKeyListener);
        };
    }, [open, onClose]);
    blockScroll();
    if (!open) {
        allowScroll();
    }
    var containerProps = {
        zIndex: zIndex
    };
    var backdropProps = {
        zIndex: zIndex - 1
    };
    var containerTransitionStyles = {
        entering: { transform: 'translateX(0)' },
        entered: { transform: 'translateX(0)' },
        exiting: { transform: "translateX(".concat(anchor === 'left' ? '-100%' : '100%', ")") },
        exited: { transform: "translateX(".concat(anchor === 'left' ? '-100%' : '100%', ")") }
    };
    var backdropTransitionStyles = {
        entering: { opacity: 0.5 },
        entered: { opacity: 0.5 },
        exiting: { opacity: 0 },
        exited: { opacity: 0 }
    };
    if (anchor === 'left') {
        containerProps.left = 0;
    }
    else {
        containerProps.right = 0;
    }
    containerProps.boxShadow = '16dp';
    containerProps.transform = "translateX(".concat(anchor === 'left' ? '-100%' : '100%', ")");
    containerProps.transition = "transform ".concat(transitionDuration, " ").concat(transitionType);
    backdropProps.transition = "opacity ".concat(transitionDuration, " ").concat(transitionType);
    backdropProps.opacity = '0.5';
    var handleClickClose = function (event) {
        if (onClose && open) {
            onClose(event, 'backdropClick');
        }
        return event;
    };
    return createPortal(jsxs(Fragment, { children: [jsx(Transition, __assign({ appear: open, in: open, timeout: duration, mountOnEnter: true, unmountOnExit: true }, { children: function (state) { return (jsx(Box, __assign({ sx: __assign(__assign(__assign(__assign({}, defaultContainerStyleProps), containerProps), containerStyleProps), containerTransitionStyles[state]), "data-testid": id ? "".concat(id, "_drawerContainer") : undefined }, { children: children }))); } })), jsx(Transition, __assign({ appear: open, in: open, timeout: duration, mountOnEnter: true, unmountOnExit: true }, { children: function (state) { return (jsx(Box, { sx: __assign(__assign(__assign({}, defaultBackdropStyleProps), backdropProps), backdropTransitionStyles[state]), "data-testid": id ? "".concat(id, "_drawerBackdrop") : undefined, onClick: handleClickClose })); } }))] }), document.body);
});

export { Avatar, Badge, Box, Button$1 as Button, Divider, Drawer, Funnel, KeyMetric, KeyMetrics, LMThemeProvider$1 as LMThemeProvider, Link, Loader, Profile, SvgIcon, Tab, Table$1 as Table, Tabs, Tooltip, TooltipContent, Typography, getFontSizeValue, getFromTheme, getThemeProp, theme, useLMTheme, useNumberFormatter };
